import type { ApiError } from '../api/client';
import {
  WEB_ROLES,
  type CabinetLanguage,
  type DeliveryMode,
  type UserCreateInput,
  type UserPatch,
  type UserView,
  type WebRole,
} from '../api/endpoints';
import { formatPhoneInput, isPhoneComplete, phoneToE164 } from './phone';

/**
 * Форма пользователя кабинета — администратора или логиста (ADR-0030).
 *
 * Курьер здесь не заводится: у него свои поля и своя форма, а в кабинет он не входит.
 *
 * Минимальная длина пароля — параметр инсталляции `cabinet_password_min_length`
 * (П-И, NFR-922), а не число в коде: форма получает его снаружи. Не удалось узнать —
 * проверяется только непустота, остальное скажет сервер (`minLength` у поля пароля).
 */

export const USER_NAME_MAX = 200;
/**
 * Длина сгенерированного пароля. Это не порог политики (его задаёт параметр), а
 * удобство: шестнадцать знаков из алфавита без похожих символов надёжны и ещё
 * диктуются по телефону.
 */
export const GENERATED_PASSWORD_LENGTH = 16;

/** Предел bcrypt и схемы `UserCreate.password` (`maxLength: 72`). */
export const PASSWORD_MAX = 72;

/** Языки интерфейса кабинета — теги локали (`CabinetLanguage`), как у самого кабинета. */
export const USER_LANGUAGES = ['uz-Latn', 'ru'] as const satisfies readonly CabinetLanguage[];
export type UserLanguage = CabinetLanguage;

export interface UserFormState {
  readonly full_name: string;
  readonly phone: string;
  readonly role: WebRole;
  readonly language: UserLanguage;
  readonly modes: readonly DeliveryMode[];
  readonly is_active: boolean;
  /** Только при заведении; смена пароля — отдельное действие. */
  readonly password: string;
}

export type UserField = 'full_name' | 'phone' | 'role' | 'modes' | 'password';

/** Код ошибки поля — ключ в `messages.cabinet.userForm.errors`. */
export type UserFieldError =
  | 'fullNameRequired'
  | 'fullNameTooLong'
  | 'phoneInvalid'
  | 'phoneTaken'
  | 'modesRequired'
  | 'passwordRequired'
  | 'passwordTooShort'
  | 'passwordTooLong'
  | 'roleInvalid'
  | 'departmentForbidden';

export type UserFormErrors = Partial<Record<UserField, UserFieldError>>;

export function emptyUserForm(departments: readonly DeliveryMode[]): UserFormState {
  return {
    full_name: '',
    phone: formatPhoneInput(''),
    role: 'dispatcher',
    language: 'uz-Latn',
    modes: departments.length === 1 ? [...departments] : [],
    is_active: true,
    password: '',
  };
}

export function userFormFrom(user: UserView): UserFormState {
  return {
    full_name: user.full_name,
    phone: formatPhoneInput(user.phone),
    role: user.role === 'admin' ? 'admin' : 'dispatcher',
    language: user.language === 'ru' ? 'ru' : 'uz-Latn',
    modes: [...user.modes],
    is_active: user.is_active,
    password: '',
  };
}

export function isWebRole(value: string): value is WebRole {
  return (WEB_ROLES as readonly string[]).includes(value);
}

/**
 * Проверка пароля отдельно от формы: ей же пользуется окно сброса пароля.
 * `minLength` — `null`, если параметр узнать не удалось.
 */
export function validatePassword(
  password: string,
  minLength: number | null,
): 'passwordRequired' | 'passwordTooShort' | 'passwordTooLong' | null {
  if (password === '') {
    return 'passwordRequired';
  }

  if (password.length > PASSWORD_MAX) {
    return 'passwordTooLong';
  }

  if (minLength !== null && password.length < minLength) {
    return 'passwordTooShort';
  }

  return null;
}

export function validateUserForm(
  form: UserFormState,
  options: {
    readonly editing: boolean;
    readonly passwordMinLength: number | null;
    /** Отделы до правки: снять все разрешено только тому, у кого их и не было. */
    readonly originalModes?: readonly DeliveryMode[] | undefined;
  },
): UserFormErrors {
  const errors: UserFormErrors = {};
  const name = form.full_name.trim();

  if (name === '') {
    errors.full_name = 'fullNameRequired';
  } else if (name.length > USER_NAME_MAX) {
    errors.full_name = 'fullNameTooLong';
  }

  // Телефон — логин и в правке не меняется: проверять нечего.
  if (!options.editing && !isPhoneComplete(form.phone)) {
    errors.phone = 'phoneInvalid';
  }

  if (!isWebRole(form.role)) {
    errors.role = 'roleInvalid';
  }

  // Без отдела пользователь входит, но не видит данных (ADR-0030). Заводить такого
  // незачем, и `UserCreate`/`UserPatch` требуют хотя бы один отдел (`minItems: 1`).
  // Исключение — правка того, у кого отделов и не было: поле не меняется и не уходит.
  const untouchedEmpty = options.editing && (options.originalModes ?? []).length === 0;

  if (form.modes.length === 0 && !untouchedEmpty) {
    errors.modes = 'modesRequired';
  }

  if (!options.editing) {
    const password = validatePassword(form.password, options.passwordMinLength);

    if (password !== null) {
      errors.password = password;
    }
  }

  return errors;
}

export function hasUserErrors(errors: UserFormErrors): boolean {
  return Object.keys(errors).length > 0;
}

/** Тело `POST /web/users`. Пароль уходит как есть: пробелы в пароле — часть пароля. */
export function toUserCreateInput(form: UserFormState): UserCreateInput {
  return {
    phone: phoneToE164(form.phone),
    full_name: form.full_name.trim(),
    role: form.role,
    password: form.password,
    language: form.language,
    modes: [...form.modes],
  };
}

/**
 * Тело `PATCH /web/users/{id}`: только изменённое — по той же причине, что у курьера:
 * правка имени не должна переписывать отделы, которые параллельно поменял другой.
 */
export function toUserPatch(form: UserFormState, original: UserFormState): UserPatch {
  const result: { -readonly [K in keyof UserPatch]: UserPatch[K] } = {};
  const name = form.full_name.trim();

  if (name !== original.full_name.trim()) {
    result.full_name = name;
  }

  if (form.role !== original.role) {
    result.role = form.role;
  }

  if (form.is_active !== original.is_active) {
    result.is_active = form.is_active;
  }

  if (form.language !== original.language) {
    result.language = form.language;
  }

  const sameModes =
    form.modes.length === original.modes.length &&
    form.modes.every((mode) => original.modes.includes(mode));

  if (!sameModes) {
    result.modes = [...form.modes];
  }

  return result;
}

/**
 * Пароль из криптостойкого генератора. Алфавит без похожих знаков (`0/O`, `1/l/I`):
 * пароль передают голосом или переписывают с экрана, и путаница в одном знаке — это
 * звонок «пароль не подходит».
 */
const PASSWORD_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789';

export function generatePassword(
  length: number = GENERATED_PASSWORD_LENGTH,
  random: (bytes: Uint8Array) => Uint8Array = (bytes) => crypto.getRandomValues(bytes),
): string {
  const alphabet = PASSWORD_ALPHABET.length;
  // Отбрасывание байтов за последним полным кругом алфавита убирает перекос
  // распределения: иначе первые буквы выпадали бы чаще остальных.
  const limit = Math.floor(BYTE_VALUES / alphabet) * alphabet;
  let result = '';

  while (result.length < length) {
    for (const byte of random(new Uint8Array(length))) {
      if (byte < limit && result.length < length) {
        result += PASSWORD_ALPHABET[byte % alphabet];
      }
    }
  }

  return result;
}

const BYTE_VALUES = 256;

/** Длина пароля для генератора: не короче политики инсталляции. */
export function generatedLength(minLength: number | null): number {
  return Math.max(GENERATED_PASSWORD_LENGTH, minLength ?? 0);
}

const SERVER_FIELDS: Readonly<Record<string, UserField>> = {
  full_name: 'full_name',
  phone: 'phone',
  role: 'role',
  modes: 'modes',
  password: 'password',
};

const FIELD_FALLBACK: Readonly<Record<UserField, UserFieldError>> = {
  full_name: 'fullNameRequired',
  phone: 'phoneInvalid',
  role: 'roleInvalid',
  modes: 'modesRequired',
  password: 'passwordTooShort',
};

/**
 * Отказ сервера → ошибки по полям. `cannot_modify_self` — не ошибка поля: сервер не даёт
 * администратору отключить себя или снять с себя роль, и сказать это нужно общим
 * блоком, словами (`general` с кодом `self`).
 */
export function userErrorsFromProblem(error: ApiError): {
  readonly fields: UserFormErrors;
  readonly general: boolean;
  readonly self: boolean;
} {
  switch (error.code) {
    case 'phone_taken':
      return { fields: { phone: 'phoneTaken' }, general: false, self: false };
    case 'department_forbidden':
      return { fields: { modes: 'departmentForbidden' }, general: false, self: false };
    case 'cannot_modify_self':
      return { fields: {}, general: true, self: true };
    // Учётную запись курьера ведёт панель курьера, не эта (`PATCH /web/drivers/{id}`).
    case 'user_is_driver':
      return { fields: { role: 'roleInvalid' }, general: true, self: false };
    default:
      break;
  }

  const fields: UserFormErrors = {};

  for (const item of error.problem.errors ?? []) {
    const field = SERVER_FIELDS[item.field.split('.')[0] ?? ''];

    if (field !== undefined) {
      fields[field] = FIELD_FALLBACK[field];
    }
  }

  return { fields, general: true, self: false };
}
